import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useController } from 'react-hook-form';
import CreatableSelect from 'react-select/creatable';
import Joi from 'joi-browser';
import { isEmailValid } from 'common/utils/helpers';
import Label from 'components/shared/Label';
import { ALERT_NOTIFICATION_FIELDS_ERRORS } from '../../constants';
import {
  checkReceiverDuplication,
  convertPhoneNumberToNumber,
  getFormattedPhoneNumber,
  getValueFromReference
} from '../../utils';
import { validateEmailNotificationsSchema, validateNotificationsSchema, validatePhoneNotificationsSchema } from '../../validations';
import './InputNotificationField.css';

const styles = {
  valueContainer: (base, props) => ({
    ...base,
    fontSize: props.selectProps.isError ? '13px' : 'inherit',
    paddingLeft: '10px'
  }),
  input: (base, props) => ({
    ...base,
    color: props.selectProps.isError ? 'var(--errorRed)' : 'var(--ibmsTextColor_1)',
    fontWeight: props.selectProps.isError ? 400 : 300
  }),
  control: (base) => ({
    ...base,
    maxHeight: '102px',
    minHeight: '34px',
    overflowY: 'auto'
  })
};

const createOption = (label) => ({
  label,
  value: label
});

const IndicatorsContainer = () => {
  return null;
};

const DropdownIndicator = () => {
  return null;
};

const CustomCreatableComponent = ({
  selectedItems,
  inputValue,
  placeholder,
  isError,
  setSelectedItems,
  handleInputChange,
  handleKeyDown
}) => (
  <CreatableSelect
    className="react-select-container"
    classNamePrefix={!isError ? 'react-select' : 'react-select-error'}
    isMulti
    isClearable={false}
    menuIsOpen={false}
    isError={isError}
    value={selectedItems}
    inputValue={inputValue}
    placeholder={placeholder}
    components={{ IndicatorsContainer, DropdownIndicator }}
    onChange={setSelectedItems}
    onInputChange={handleInputChange}
    onKeyDown={handleKeyDown}
    styles={styles}
    backspaceRemovesValue={false}
  />
);

function InputNotificationField({ name, label, placeholder, errors, isTooltipShow }) {
  const {
    field: { value, onChange }
  } = useController({ name });
  const [currentInputValue, setCurrentInputValue] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const InputNotificationFieldWrapperRef = useRef(null);
  const isHintShow = selectedItems.length < 2 && currentInputValue.length > 6;

  const calcTooltipPosition = () => {
    const coefficient = 6;
    const paddingLeft = 24;
    const marginLeft = 40;
    const tooltipLength = 130;
    let position = currentInputValue.length * coefficient - marginLeft;

    if (selectedItems.length > 0) {
      const firstElementLength = selectedItems[0].value.length * coefficient + paddingLeft;
      position = firstElementLength + position;
    }

    const fieldWidth = getValueFromReference(InputNotificationFieldWrapperRef, 'width');
    return position + tooltipLength > fieldWidth ? fieldWidth - tooltipLength : position;
  };

  const checkZeroStartedPhoneNumber = (digitalPhoneNumber) => {
    if (digitalPhoneNumber.charAt(0) === '0') {
      setErrorMessage(ALERT_NOTIFICATION_FIELDS_ERRORS.INVALID_FORMAT);
      setIsError(true);
      return false;
    }

    return true;
  };

  const isDuplicated = useCallback(
    (v) => {
      if (checkReceiverDuplication(v, selectedItems)) {
        setErrorMessage(ALERT_NOTIFICATION_FIELDS_ERRORS.DUPLICATED_RECIEVER);
        setIsError(true);
        return true;
      }
      return false;
    },
    [selectedItems]
  );

  const isPassedValidation = (v, notificationSchema) => {
    const payload = { notifications: [v] };
    const result = Joi.validate(payload, notificationSchema, { abortEarly: false });
    const { error } = result;

    if (error) {
      setErrorMessage(ALERT_NOTIFICATION_FIELDS_ERRORS.INVALID_FORMAT);
      setIsError(true);
      return false;
    }

    return true;
  };

  const addSelectedItems = (option) => {
    setSelectedItems((prev) => [...prev, createOption(option)]);
    setCurrentInputValue('');
  };

  const handleInputChange = (inputValue) => {
    setCurrentInputValue(inputValue);
    setIsError(false);
  };

  const handleKeyDown = (event) => {
    if (!currentInputValue) return;

    if (event.key === 'Enter') {
      addOption();
      event.preventDefault();
    }
  };

  // Add e-mail
  const addEmailValidOption = useCallback(
    (email) => {
      if (!isPassedValidation(email, validateEmailNotificationsSchema)) {
        return;
      }

      if (!isDuplicated(email)) {
        addSelectedItems(email);
      }
    },
    [isDuplicated]
  );

  // Add phone number
  const addPhoneNumberValidOption = useCallback(
    (phoneNumber) => {
      const digitalPhoneNumber = convertPhoneNumberToNumber(phoneNumber);

      if (!isPassedValidation(digitalPhoneNumber, validatePhoneNotificationsSchema)) {
        return;
      }

      if (!checkZeroStartedPhoneNumber(digitalPhoneNumber)) {
        return;
      }

      if (isDuplicated(digitalPhoneNumber)) {
        return;
      }

      addSelectedItems(getFormattedPhoneNumber(digitalPhoneNumber));
    },
    [isDuplicated]
  );

  // Add new notification option
  const addOption = useCallback(() => {
    const trimmedInputValue = currentInputValue.trim();

    if (isEmailValid(trimmedInputValue)) {
      addEmailValidOption(trimmedInputValue);
      return;
    }

    addPhoneNumberValidOption(trimmedInputValue);
  }, [addEmailValidOption, addPhoneNumberValidOption, currentInputValue]);

  useEffect(() => {
    onChange(selectedItems.map((item) => item.value.trim()));

    if (isError) {
      // Error event is occured and some of the selectedItems is removed
      addOption();
      setIsError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onChange, selectedItems]);

  useEffect(() => {
    if (value) {
      const currentItems = value.map((item) => createOption(item));
      setSelectedItems(currentItems);
    } else {
      setSelectedItems([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Showing outer error
  useEffect(() => {
    if (!errors) {
      return;
    }

    if (errors?.notifications) {
      setErrorMessage(ALERT_NOTIFICATION_FIELDS_ERRORS.INVALID_FORMAT);
      setIsError(true);
    }
  }, [errors]);

  return (
    <div className="InputNotificationField_Wrapper" ref={InputNotificationFieldWrapperRef}>
      <Label name={name} text={label} />
      <CustomCreatableComponent
        name={name}
        placeholder={placeholder}
        selectedItems={selectedItems}
        inputValue={currentInputValue}
        handleInputChange={handleInputChange}
        handleKeyDown={handleKeyDown}
        setSelectedItems={setSelectedItems}
        isError={isError}
      />

      <div className="InputNotificationField_ErrorMessage InputField_Custom_ErrorMessage">
        {isError && <div>{errorMessage}</div>}
      </div>

      {isTooltipShow && isHintShow && !isError && (
        <div className="InputNotificationField_Tooltip" style={{ left: calcTooltipPosition() }}>
          Press Enter to add
        </div>
      )}
    </div>
  );
}

export default InputNotificationField;
